import { getReports } from "../../../services/expenses";
import { onQuerySuccess } from "../../../utils/queryResponse";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from 'react-i18next';
import JobsPerformanceFilter from "./jobsPerformanceFilter";
import { toTitleCase, rsdFormat } from "../../../utils/helper"
import { get as generateNiceColors } from "../../../utils/niceColors"
import Skeleton from "../../../components/skeleton";

const AMBER = '#B45309';

const WalletIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></svg>
);
const TagIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
);
const CardIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
);

/* Horizontal ranked-bar breakdown card */
const BarBreakdownCard = ({ title, headIcon, rows, colors, total, isFetching }) => {
    const { t } = useTranslation();
    const max = rows.length ? Math.max(...rows.map(r => r.amount)) : 0
    const loading = isFetching && rows.length === 0
    return (
        <div className="card-sbr" style={{ position: 'relative' }}>
            <div className="ch">
                {headIcon}
                <span className="t" style={{ fontWeight: 800, color: 'var(--ink)', fontSize: '14px' }}>{title}</span>
            </div>
            {loading ? (
                <div className="exp-bars">
                    {['72%', '58%', '44%', '30%'].map((w, i) => (
                        <div className="exp-bar-row" key={i}>
                            <Skeleton width="22%" height={13} />
                            <span className="exp-bar-track"><Skeleton width={w} height={13} /></span>
                            <Skeleton width="14%" height={13} />
                        </div>
                    ))}
                </div>
            ) : rows.length > 0 ? (
                <div className="exp-bars">
                    {rows.map((r, i) => {
                        const pct = total > 0 ? Math.round((r.amount / total) * 100) : 0
                        const w = max > 0 ? Math.max((r.amount / max) * 100, 3) : 0
                        return (
                            <div key={r.label} className="exp-bar-row" style={{ padding: '9px 0', borderBottom: '1px solid var(--line-soft)' }}>
                                <span className="exp-bar-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: 'var(--text)' }} title={r.label}>
                                    <span style={{ fontSize: '12.5px', fontWeight: 800, color: i === 0 ? 'var(--brand)' : 'var(--faint)', flexShrink: 0 }}>{i + 1}</span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
                                </span>
                                <span className="exp-bar-track track" style={{ height: '8px' }}>
                                    <i style={{ display: 'block', width: `${w}%`, height: '100%', background: colors[i] }}></i>
                                </span>
                                <span className="exp-bar-val num" style={{ fontWeight: 800, color: 'var(--ink)' }}>
                                    {rsdFormat(r.amount)}
                                    <span className="exp-bar-pct" style={{ marginLeft: '5px' }}>· {pct}%</span>
                                </span>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className="sbr-empty">
                    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    {t('reports.noDataForPeriod')}
                </div>
            )}
        </div>
    )
}

const ExpensesReportsPage = () => {

    const [reports, setReports] = useState({ expensesByCategory: [], expensesByPaymentMethod: [] })
    const { t } = useTranslation();
    const [filters, setFilters] = useState(['', ''])

    const { data, isFetching } = useQuery(
        ['expenses-reports', ...filters],
        () => getReports(...filters)
    )

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setReports(result)
        })
    }, [data])

    /* ── Derived, sorted breakdowns ── */
    const byCategory = useMemo(
        () => [...(reports.expensesByCategory || [])]
            .map(x => ({ label: toTitleCase(x.category ?? 'Uncategorized'), amount: Number(x.amount) || 0 }))
            .sort((a, b) => b.amount - a.amount),
        [reports]
    )
    const byPayment = useMemo(
        () => [...(reports.expensesByPaymentMethod || [])]
            .map(x => ({ label: toTitleCase(x.paymentMethod ?? 'Unspecified'), amount: Number(x.amount) || 0 }))
            .sort((a, b) => b.amount - a.amount),
        [reports]
    )
    const total = useMemo(() => byCategory.reduce((s, x) => s + x.amount, 0), [byCategory])
    const topCat = byCategory[0]
    const topCatPct = total > 0 && topCat ? Math.round((topCat.amount / total) * 100) : 0
    const topPay = byPayment[0]
    const topPayPct = total > 0 && topPay ? Math.round((topPay.amount / total) * 100) : 0
    const catColors = useMemo(() => generateNiceColors(byCategory.length || 1), [byCategory.length])
    const payColors = useMemo(() => generateNiceColors(byPayment.length || 1), [byPayment.length])

    const summaryLoading = isFetching && !byCategory.length

    return (
        <div className="sbr">
            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('reports.expensesReports')}</div>
                    <div className="sub">{t('reports.expensesSubtitle')}</div>
                </div>
            </div>

            {/* Filters */}
            <div style={{ marginBottom: '14px' }}>
                <JobsPerformanceFilter onFilter={setFilters} />
            </div>

            {/* KPI tiles */}
            <div className="kpis3" style={{ marginBottom: '18px' }}>
                <div className="kpi lift">
                    <div className="ico" style={{ background: 'rgba(255,255,255,.12)', borderColor: 'rgba(255,255,255,.22)', color: '#fff' }}>
                        <WalletIcon />
                    </div>
                    <div className="l">{t('reports.totalSpent')}</div>
                    {summaryLoading
                        ? <Skeleton width="64%" height={26} style={{ margin: '8px 0 2px' }} />
                        : <div className="n num">{rsdFormat(total)}</div>}
                    <div className="d flat" style={{ color: '#A9B6CE' }}>{t('reports.categoriesCount', { count: byCategory.length })}</div>
                </div>

                <div className="kpi b1">
                    <div className="ico">
                        <TagIcon />
                    </div>
                    <div className="l">{t('reports.topCategory')}</div>
                    {summaryLoading
                        ? <Skeleton width="64%" height={26} style={{ margin: '8px 0 2px' }} />
                        : <div className="n sm">{topCat?.label || '—'}</div>}
                    <div className="d flat">{topCat ? t('reports.ofTotal', { amount: rsdFormat(topCat.amount), pct: topCatPct }) : t('reports.noData')}</div>
                </div>

                <div className="kpi b2">
                    <div className="ico">
                        <CardIcon />
                    </div>
                    <div className="l">{t('reports.topPaymentMethod')}</div>
                    {summaryLoading
                        ? <Skeleton width="64%" height={26} style={{ margin: '8px 0 2px' }} />
                        : <div className="n sm">{topPay?.label || '—'}</div>}
                    <div className="d flat">{topPay ? t('reports.ofTotal', { amount: rsdFormat(topPay.amount), pct: topPayPct }) : t('reports.noData')}</div>
                </div>
            </div>

            {/* Breakdown ranked bars */}
            <div className="cols">
                <BarBreakdownCard
                    title={t('reports.byCategory')}
                    headIcon={
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand)', flexShrink: 0 }}>
                            <line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/>
                        </svg>
                    }
                    rows={byCategory} colors={catColors} total={total} isFetching={isFetching}
                />
                <BarBreakdownCard
                    title={t('reports.byPaymentMethod')}
                    headIcon={
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand)', flexShrink: 0 }}>
                            <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                        </svg>
                    }
                    rows={byPayment} colors={payColors} total={total} isFetching={isFetching}
                />
            </div>
        </div>
    );
};

export default ExpensesReportsPage;
